import './NewDeckModal.scss';
import cn from 'classnames';
import { TemplateSummary } from '@blisspointmedia/bpm-types/dist/SlidesV2';
import { useMemo, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { Modal } from 'react-bootstrap';
import { Button } from '../../Components/Button';
import { Dropdown } from '../../Components/Dropdown';
import { IconButton } from '../../Components/IconButton';
import { FilterBar } from '../../Components/FilterBar';
import { InteractiveDiv } from '../../Components/InteractiveEl';
import { Appearance, Design, Placement, Size, StretchMode } from '../../Components/designConstants';
import { dateToMddyy } from '../../utils/date-utils';
import { SlideThumbnail } from './SlideThumbnail';

interface NewDeckModalProps {
  show: boolean;
  blueprints: TemplateSummary[];
  onHide: () => void;
  onSubmit: (templateId: string, templateSummary: TemplateSummary) => void;
}

const SORT_OPTIONS = [
  { label: 'Abc', value: 'abc' },
  { label: 'Newest First', value: 'newest' },
  { label: 'Oldest First', value: 'oldest' }
];

export const NewDeckModal = ({ show, blueprints, onHide, onSubmit }: NewDeckModalProps) => {
  const [selectedBlueprint, setSelectedBlueprint] = useState<TemplateSummary | null>(null);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0].value);
  const [submitting, setSubmitting] = useState(false);
  const [filterBlueprints, setFilterBlueprints] = useState<{
    filter: (line: TemplateSummary) => boolean;
  }>({
    filter: () => true
  });

  const filteredAndSortedBlueprints = useMemo(() => {
    const filtered = blueprints.filter(filterBlueprints.filter);
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === 'abc') {
        return a.blueprintName.localeCompare(b.blueprintName);
      }
      const aMs = Date.parse(a.updatedAt);
      const bMs = Date.parse(b.updatedAt);
      if (sortBy === 'newest') {
        return bMs - aMs;
      }
      if (sortBy === 'oldest') {
        return aMs - bMs;
      }
      return 0;
    });
    return sorted;
  }, [blueprints, filterBlueprints, sortBy]);

  // Initialize selected blueprint when modal opens or blueprints change. Using a derived
  // value rather than a setState-in-effect avoids a cascading render on open.
  const activeBlueprint = selectedBlueprint ?? (show ? filteredAndSortedBlueprints[0] : null);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size='xl'
      centered
      className='new-deck-modal'
      data-testid='new-deck-modal'
    >
      <Modal.Header>
        <Modal.Title className='new-deck-modal-title'>Choose Blueprint</Modal.Title>
        <IconButton
          className='new-deck-modal-close'
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          onClick={onHide}
        >
          <MdClose />
        </IconButton>
      </Modal.Header>
      <Modal.Body className='new-deck-modal-body'>
        <div className='new-deck-modal-search-filter'>
          <FilterBar
            size={Size.LG}
            options={['blueprintName']}
            lines={blueprints}
            onFilter={filter => {
              setFilterBlueprints({ filter });
            }}
          />
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            options={SORT_OPTIONS}
            onChange={setSortBy}
            label='Sort by'
            value={sortBy}
            stretch={StretchMode.FIT}
          />
        </div>
        <div className='new-deck-modal-content'>
          <div className='new-deck-sidebar'>
            <div className='sidebar-list'>
              {filteredAndSortedBlueprints.map(blueprint => (
                <BlueprintListItem
                  key={`${blueprint.blueprintId}-${blueprint.configId}`}
                  blueprint={blueprint}
                  isSelected={
                    activeBlueprint?.blueprintId === blueprint.blueprintId &&
                    activeBlueprint?.configId === blueprint.configId
                  }
                  onSelect={() => setSelectedBlueprint(blueprint)}
                />
              ))}
            </div>
          </div>

          <div className='new-deck-thumbnail-list'>
            <div className='slides-grid'>
              {(activeBlueprint?.slides ?? [])
                .slice()
                .sort((a, b) => a.slideNumber - b.slideNumber)
                .map(slide => (
                  <SlideThumbnail
                    key={slide.slideId}
                    slideNumber={slide.slideNumber}
                    imageUrl={slide.imageUrl}
                  />
                ))}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className='new-deck-modal-footer'>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={onHide}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          data-testid='new-deck-submit-button'
          onClick={() => {
            if (!activeBlueprint) return;
            setSubmitting(true);
            onSubmit(activeBlueprint.blueprintId, activeBlueprint);
          }}
          disabled={submitting || !activeBlueprint}
        >
          {submitting ? 'Loading...' : 'New Deck From This Blueprint'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface BlueprintListItemProps {
  blueprint: TemplateSummary;
  isSelected: boolean;
  onSelect: () => void;
}

const BlueprintListItem = ({ blueprint, isSelected, onSelect }: BlueprintListItemProps) => (
  <InteractiveDiv
    data-testid='blueprint-list-item'
    className={cn('build-slides-item', { selected: isSelected })}
    onClick={onSelect}
  >
    <div className='item-title'>{blueprint.blueprintName}</div>
    <div className='item-description'>{blueprint.description}</div>
    <div className='item-length'>({blueprint.slides.length} Slides)</div>
    <div className='item-last-updated'>
      Last updated on {dateToMddyy(blueprint.updatedAt)} by {blueprint.updatedBy}
    </div>
  </InteractiveDiv>
);
